/**
 * Automations settings — rules that take the next step (roadmap 3.3).
 *
 * A rule is one sentence: when something happens, and only if a condition
 * holds, do one thing. The builder is that sentence with a picker in each
 * blank (RuleBuilder). An empty workspace starts from four example rules.
 * Every rule that could not do anything here says so, with the fix beside it.
 *
 * Engine: migrations 817 (rules, runs, triggers, sweep), 826 (try it) and
 * 832 (up to five actions, the ready-for-ranking trigger).
 * Words: src/lib/automationRules.ts. Data: src/lib/automationService.ts.
 *
 * Route: /prioritization/settings/automations (admins — settingsConfig).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Loader2, Pencil, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { Panel, Switch, Callout, EmptyState } from '@/components/ds';
import { btnPrimary, btnSecondary, status } from '../../../components/mono/primitives';
import { StarterCards } from '../../../components/shared/StarterCards';
import { RuleBuilder, type RuleBuilderOptions } from '../../../components/prioritization/automations/RuleBuilder';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { isPlatformOrSystemAdmin } from '@/types/prioritization/roles';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { formatRelativeTime } from '@/i18n/formatters';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { useModuleGate } from '../../../lib/moduleGate';
import { INTELLIGENCE_AGENT_LIST } from '../../../lib/intelligence/agentMeta';
import { listItemCategories } from '../../../lib/itemCategoriesService';
import { getAmbientJobs, setAmbientJobEnabled, type AmbientJob, type AmbientJobType } from '../../../lib/ambientRuntimeService';
import { eventMatches } from '../../../../supabase/functions/_shared/webhookEventMatch';
import {
  AUTOMATION_WEBHOOK_EVENT,
  STARTER_RULES,
  ACTION_SHORT,
  blankActionConfig,
  blankTriggerConfig,
  describeRule,
  ruleBlockers,
  runOutcomeNote,
  runStatusLabel,
  type AutomationDraft,
  type RuleLookups,
  type RuleReadiness,
} from '../../../lib/automationRules';
import {
  createAutomation,
  deleteAutomation,
  listAutomationRuns,
  listAutomations,
  listWebhookEventLists,
  listWorkspaceGroups,
  listWorkspaceQueues,
  previewAutomation,
  setAutomationEnabled,
  updateAutomation,
  type Automation,
  type AutomationRun,
  type SavedDraft,
} from '../../../lib/automationService';

type Editing = { id: string | 'new'; draft: AutomationDraft } | null;

function draftOf(a: Automation): AutomationDraft {
  return {
    triggerKind: a.triggerKind,
    triggerConfig: { ...a.triggerConfig },
    conditions: { ...a.conditions },
    actionKind: a.actionKind,
    actionConfig: { ...a.actionConfig },
    moreActions: a.moreActions.map((x) => ({ kind: x.kind, config: { ...x.config } })),
  };
}

function blankDraft(): AutomationDraft {
  return {
    triggerKind: 'item_exit_status_changed',
    triggerConfig: blankTriggerConfig('item_exit_status_changed'),
    conditions: {},
    actionKind: 'notify',
    actionConfig: blankActionConfig('notify'),
    moreActions: [],
  };
}

export default function Automations() {
  const addNotification = useUIStore((s) => s.addNotification);
  const can = useUserStore((s) => s.can);
  const userId = useUserStore((s) => s.userId);
  const currentRole = useUserStore((s) => s.currentRole);
  const canEdit = can('admin.settings') || (currentRole ? isPlatformOrSystemAdmin(currentRole) : false);
  const workspaceId = getCurrentWorkspaceId();
  const { users } = useSystemUsers();
  const backgroundJobsOn = useModuleGate('ambient_runtime');
  const items = useItemsStore((s) => s.items);

  const [automations, setAutomations] = useState<Automation[]>([]);
  const [runs, setRuns] = useState<AutomationRun[]>([]);
  const [queues, setQueues] = useState<Array<{ id: string; name: string; hasDefaultOwner: boolean }>>([]);
  const [groups, setGroups] = useState<Array<{ id: string; name: string }>>([]);
  const [categories, setCategories] = useState<Array<{ value: string; label: string }>>([]);
  const [webhookEventLists, setWebhookEventLists] = useState<string[][]>([]);
  const [jobs, setJobs] = useState<AmbientJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing>(null);

  const load = useCallback(async () => {
    if (!workspaceId) {
      setIsLoading(false);
      return;
    }
    const [a, r, q, g, c, w, j] = await Promise.all([
      listAutomations(workspaceId),
      listAutomationRuns(workspaceId, 50),
      listWorkspaceQueues(workspaceId),
      listWorkspaceGroups(workspaceId),
      listItemCategories(workspaceId),
      listWebhookEventLists(workspaceId),
      getAmbientJobs(workspaceId),
    ]);
    setAutomations(a.data);
    setRuns(r.data);
    setQueues(q.data);
    setGroups(g.data);
    setCategories(c.data.filter((x) => x.isActive).map((x) => ({ value: x.slug, label: x.label })));
    setWebhookEventLists(w.data);
    setJobs(j.data);
    setError(a.error ?? r.error ?? null);
    setIsLoading(false);
  }, [workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const agents = useMemo(() => INTELLIGENCE_AGENT_LIST.map((m) => ({ value: m.agentType, label: m.name })), []);

  const lookups: RuleLookups = useMemo(
    () => ({
      userName: (id: string) => users.find((u) => u.id === id)?.name || users.find((u) => u.id === id)?.email,
      queueName: (id: string) => queues.find((q) => q.id === id)?.name,
      groupName: (id: string) => groups.find((g) => g.id === id)?.name,
      categoryLabel: (slug: string) => categories.find((c) => c.value.toLowerCase() === slug.toLowerCase())?.label,
      agentName: (t: string) => agents.find((a) => a.value === t)?.label,
    }),
    [users, queues, groups, categories, agents],
  );

  const options: RuleBuilderOptions = useMemo(() => {
    const tags = new Set<string>();
    for (const it of items) for (const t of (it as { tags?: string[] }).tags ?? []) if (t) tags.add(t);
    return {
      users: users.map((u) => ({ value: u.id, label: u.name || u.email })),
      queues: queues.map((q) => ({ value: q.id, label: q.name, hasDefaultOwner: q.hasDefaultOwner })),
      groups: groups.map((g) => ({ value: g.id, label: g.name })),
      categories,
      agents,
      tags: Array.from(tags).sort((x, y) => x.localeCompare(y)).slice(0, 300),
    };
  }, [users, queues, groups, categories, agents, items]);

  const readiness: RuleReadiness = useMemo(
    () => ({
      backgroundJobsOn,
      jobsOn: Object.fromEntries(jobs.map((j) => [j.jobType, j.enabled])),
      webhookAddresses: webhookEventLists.length,
      webhookListeners: webhookEventLists.filter((ev) => eventMatches(ev, AUTOMATION_WEBHOOK_EVENT)).length,
      anyQueueHasDefaultOwner: queues.some((q) => q.hasDefaultOwner),
    }),
    [backgroundJobsOn, jobs, webhookEventLists, queues],
  );

  const save = async (saved: SavedDraft): Promise<string | null> => {
    if (!workspaceId || !editing) return 'No workspace is open.';
    const res = editing.id === 'new' ? await createAutomation(workspaceId, saved) : await updateAutomation(editing.id, saved);
    if (res.error) return res.error;
    addNotification({ type: 'success', title: editing.id === 'new' ? 'Rule saved' : 'Rule changed', message: '', duration: 2000 });
    setEditing(null);
    await load();
    return null;
  };

  const switchOnJob = async (jobType: string) => {
    if (!workspaceId) return;
    const res = await setAmbientJobEnabled({ workspaceId, jobType: jobType as AmbientJobType, enabled: true, createdBy: userId });
    if (res.error) {
      addNotification({ type: 'error', title: 'Could not switch it on', message: res.error, duration: 5000 });
      return;
    }
    const j = await getAmbientJobs(workspaceId);
    setJobs(j.data);
  };

  const toggle = async (a: Automation, enabled: boolean) => {
    setAutomations((list) => list.map((x) => (x.id === a.id ? { ...x, enabled } : x)));
    const res = await setAutomationEnabled(a.id, enabled);
    if (res.error) {
      addNotification({ type: 'error', title: 'Could not switch the rule', message: res.error, duration: 5000 });
      await load();
    }
  };

  const remove = async (a: Automation) => {
    if (!window.confirm(`Delete this rule?\n\n${describeRule(draftOf(a), lookups)}\n\nWhat it has done stays done; its history goes.`)) return;
    const res = await deleteAutomation(a.id);
    if (res.error) {
      addNotification({ type: 'error', title: 'Could not delete the rule', message: res.error, duration: 5000 });
      return;
    }
    await load();
  };

  const ruleById = (id: string) => automations.find((a) => a.id === id);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Zap className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--active-tint)]"
        title="Automations"
        subtitle="Rules that take the next step for you when something changes"
        actions={
          <Link to="/prioritization/documentation/automations" {...btnSecondary()}>
            How rules work
          </Link>
        }
      />

      {error && <Callout tone="coral" title="The rules could not be loaded">{error}</Callout>}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading rules…
        </div>
      ) : (
        <>
          {editing && canEdit && workspaceId && (
            <RuleBuilder
              key={editing.id}
              initial={editing.draft}
              isNew={editing.id === 'new'}
              lookups={lookups}
              options={options}
              readiness={readiness}
              onSwitchOnJob={switchOnJob}
              onSave={save}
              onCancel={() => setEditing(null)}
              onPreview={(itemId, draft) => previewAutomation(workspaceId, itemId, draft)}
            />
          )}

          {!editing && automations.length === 0 && (
            <StarterCards
              label="Example rules"
              lead="No rules yet. Start from one of these, and change any part of it before you save."
              useLabel="Use this rule"
              cardTestId="starter-rule"
              cards={STARTER_RULES.map((d, i) => ({
                key: String(i),
                sentence: describeRule(d, lookups),
                onUse: canEdit ? () => setEditing({ id: 'new', draft: d }) : undefined,
              }))}
            >
              {canEdit && (
                <button type="button" {...btnSecondary()} onClick={() => setEditing({ id: 'new', draft: blankDraft() })}>
                  Start from a blank rule
                </button>
              )}
            </StarterCards>
          )}

          {automations.length > 0 && (
            <Panel
              title="Rules"
              count={automations.length}
              actions={
                canEdit && !editing ? (
                  <button type="button" {...btnPrimary()} onClick={() => setEditing({ id: 'new', draft: blankDraft() })}>
                    New rule
                  </button>
                ) : null
              }
            >
              <div className="divide-y divide-[var(--rule)]">
                {automations.map((a) => {
                  const d = draftOf(a);
                  const blocked = ruleBlockers(d, readiness).length > 0;
                  return (
                    <div key={a.id} className="flex items-start gap-4 py-3" data-testid="automation-row">
                      <Switch checked={a.enabled} disabled={!canEdit} onChange={(next) => void toggle(a, next)}
                        aria-label={a.enabled ? 'Switch this rule off' : 'Switch this rule on'} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-[0.95rem] ${a.enabled ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)]'}`}>{describeRule(d, lookups)}</p>
                        <p className="mt-1 text-xs text-[var(--text-ter)]">
                          {a.enabled ? '' : 'Off · '}
                          {a.fireCount === 0
                            ? 'Has not run yet'
                            : `Ran ${a.fireCount} time${a.fireCount === 1 ? '' : 's'}, last ${a.lastFiredAt ? formatRelativeTime(a.lastFiredAt) : ''}`}
                        </p>
                        {blocked && a.enabled && (
                          <p {...status('coral', 'mt-1')}>Cannot run here yet · open it to see why</p>
                        )}
                      </div>
                      {canEdit && !editing && (
                        <div className="flex shrink-0 items-center gap-2">
                          <button type="button" {...btnSecondary()} onClick={() => setEditing({ id: a.id, draft: d })} aria-label="Change this rule">
                            <Pencil className="h-3.5 w-3.5" /> Change
                          </button>
                          <button type="button" {...btnSecondary()} onClick={() => void remove(a)} aria-label="Delete this rule">
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}

          {runs.length > 0 && (
            <Panel title="What the rules did" count={runs.length}>
              <div className="divide-y divide-[var(--rule)]">
                {runs.map((r) => {
                  const rule = ruleById(r.automationId);
                  const note = runOutcomeNote(r.status, r.detail);
                  return (
                    <div key={r.id} className="flex items-start gap-4 py-2.5 text-sm" data-testid="automation-run">
                      <span className="w-28 shrink-0 text-xs text-[var(--text-ter)]">{formatRelativeTime(r.createdAt)}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[var(--text-pri)]">
                          {r.subjectKind === 'item' ? (
                            <Link to={`/prioritization/items/${r.subjectId}`} className="underline">{r.subjectTitle || 'An item'}</Link>
                          ) : r.subjectKind === 'cycle' ? (
                            <Link to={`/prioritization/cycles/${r.subjectId}`} className="underline">{r.subjectTitle || 'A cycle'}</Link>
                          ) : (
                            <span>{r.subjectTitle || 'A finding'}</span>
                          )}
                          <span className="text-[var(--text-ter)]"> · {ACTION_SHORT[r.actionKind] ?? r.actionKind} · {runStatusLabel(r.status)}</span>
                        </p>
                        <p className="text-xs text-[var(--text-ter)]">
                          {rule ? describeRule(draftOf(rule), lookups) : 'A rule since deleted.'}
                          {note ? ` ${note}` : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}

          {!editing && automations.length > 0 && runs.length === 0 && (
            <EmptyState size="inline" title="No rule has run yet" description="When one does, what it did shows here, including the times it had nothing to do." />
          )}
        </>
      )}
    </div>
  );
}
